import {
  CalendarIcon,
  HashIcon,
  MapPinnedIcon,
  PackageIcon,
  UserIcon,
  WarehouseIcon,
} from 'lucide-react';

import type { AddressDTO } from '@invenco/common-interface/shared';
import type { ReturnDTO } from '@invenco/supply-interface';

import { AddressDisplay } from '@/components/address-display/address-display';
import { Badge } from '@/components/core/badge';
import { DetailList } from '@/components/detail-list';
import { formatDate } from '@/shared/helpers';
import { returnCategoryTitle } from '@/shared/title-maps';

type Props = {
  returnDto?: ReturnDTO;
  isLoading?: boolean;
};

export function ReturnDetailsSection({ returnDto, isLoading }: Props) {
  const fromAddress = returnDto?.fromAddress;
  // Show only the company name (when present) and the address lines; omit the
  // contact name/number/email by passing a plain AddressDTO (no contact fields).
  const companyAddress: AddressDTO | undefined = fromAddress
    ? {
        companyName: fromAddress.companyName,
        address1: fromAddress.address1,
        address2: fromAddress.address2,
        city: fromAddress.city,
        state: fromAddress.state,
        postcode: fromAddress.postcode,
        country: fromAddress.country,
      }
    : undefined;

  return (
    <DetailList
      title="Details"
      isLoading={isLoading}
      items={[
        { icon: WarehouseIcon, label: 'Location', value: returnDto?.locationName },
        {
          icon: MapPinnedIcon,
          label: 'From address',
          value: <AddressDisplay address={companyAddress} isLoading={isLoading} />,
        },
        { icon: UserIcon, label: 'Customer', value: returnDto?.fromAddress?.contactName },
        {
          icon: PackageIcon,
          label: 'Return type',
          value: returnDto?.category ? (
            <Badge variant="secondary">{returnCategoryTitle[returnDto.category]}</Badge>
          ) : undefined,
        },
        { icon: HashIcon, label: 'Tracking number', value: returnDto?.trackingNumber },
        {
          icon: CalendarIcon,
          label: 'Date created',
          value: returnDto?.createdAt ? formatDate(returnDto.createdAt) : undefined,
        },
        {
          icon: CalendarIcon,
          label: 'Date closed',
          value: returnDto?.closedAt ? formatDate(returnDto.closedAt) : undefined,
        },
      ]}
    />
  );
}
